import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { useSearchParams } from 'react-router-dom'
import './CollectionsDashboard.css'
import {
    ALL_COLLECTION_CASES,
    COLLECTION_CASES_COUNT,
    COLLECTION_BRANCHES,
    COLLECTIONS_SAVED_VIEWS,
    UPDATE_COLLECTIONS_SAVED_VIEWS,
} from './queries'
import CaseRow from './CaseRow'
import AdminOverview from './AdminOverview'
import FilterPopover from './FilterPopover'
import SavedViewsButton from './SavedViewsButton'
import ExternalIntegrations from '../../Driver/Detail/DetailsNew/Components/ExternalIntegrations'
import { toGlobalId, weekdayLong } from './helpers'

const TABS = [
    { id: 'OPEN', label: 'Collections', glyph: '$' },
    { id: 'CLOSED', label: 'Resolved', glyph: '✓' },
]

const PAGE_SIZE = 30

const COLUMNS = [
    { id: 'idx', label: '#' },
    { id: 'driver', label: 'Driver' },
    { id: 'branch', label: 'Branch' },
    { id: 'assigned', label: 'Assigned' },
    { id: 'status', label: 'Status' },
    { id: 'created_at', label: 'Created', sortable: true },
    {
        id: 'opening_balance',
        label: 'Opening Balance',
        sortable: true,
        tooltip: 'The amount owed when this collection case was opened.',
    },
    {
        id: 'balance',
        label: 'Current Charge',
        sortable: true,
        tooltip: 'Current outstanding balance: opening balance minus amount collected so far.',
    },
    {
        id: 'retries',
        label: 'Attempts',
        tooltip: 'Automated charge attempts made so far, out of the maximum allowed before the case is marked exhausted.',
    },
    {
        id: 'next_charge',
        label: 'Next charge',
        sortable: true,
        tooltip: 'When the next automated charge attempt is scheduled to fire (per backoff). Null once attempts are exhausted or the case closes.',
    },
    { id: 'latest_activity', label: 'Latest activity', sortable: true },
    {
        id: 'billing_notes',
        label: 'Billing Notes',
        tooltip: "The driver's weekly billing notes. Click the pencil to view history and edit.",
    },
    { id: 'actions', label: '' },
]

// Filter keys that live as URL params. Values are always strings in the URL.
const FILTER_KEYS = [
    'branchId',
    'branchIds',
    'driverBillingDay',
    'driverStages',
    'carStatusIn',
    'driverTags',
    'hasPaymentPlan',
    'hasPaymentSchedule',
    'driverInOffice',
    'openedWithinDays',
    'openedAtDateGte',
    'openedAtDateLte',
    'openingBalanceGte',
    'openingBalanceLte',
    'retriesExhausted',
    'assignedToId',
    'assignedToIds',
    'autoCharge',
]

const STORAGE_KEY = 'CollectionsDashboardState'

// Driver stage choices that match the backend's DRIVER_STAGES enum.
const DRIVER_STAGE_OPTIONS = [
    { value: -3, label: 'Unknown' },
    { value: -1, label: 'Denied' },
    { value: 0, label: "Didn't take a car" },
    { value: 1, label: 'Applied' },
    { value: 3, label: 'Insurance Approved' },
    { value: 5, label: 'Awaiting Approval' },
    { value: 6, label: 'Active' },
    { value: 10, label: 'Has Rented' },
]
const STAGE_LABEL_BY_VALUE = Object.fromEntries(
    DRIVER_STAGE_OPTIONS.map((o) => [o.value, o.label]),
)

// JS getDay → 0=Sun..6=Sat. Driver.WEEKDAYS encodes Mon=0..Sun=6.
function todayWeekday() {
    return (new Date().getDay() + 6) % 7
}

function defaultFilters() {
    return {
        driverStages: [6],
        driverBillingDay: todayWeekday(),
    }
}

const BALANCE_KEYS = ['openingBalanceGte', 'openingBalanceLte']
const OPENED_KEYS = ['openedWithinDays', 'openedAtDateGte', 'openedAtDateLte']

// Quick-pick relative ranges. Value = number of days going back from today.
// Stored on the filters object as `openedWithinDays`; absolute date fields
// remain unset so the relative range follows "today" as time passes.
const OPENED_PRESETS = [
    { value: 1, label: 'Today' },
    { value: 7, label: 'Last 7 days' },
    { value: 30, label: 'Last 30 days' },
    { value: 90, label: 'Last 90 days' },
]

const FILTER_LABELS = {
    branchId: 'Branch',
    branchIds: 'Branches',
    driverBillingDay: 'Billing day',
    driverStages: 'Driver stage',
    carStatusIn: 'Car status',
    driverTags: 'Driver tags',
    hasPaymentPlan: 'Payment plan',
    hasPaymentSchedule: 'Payment scheduled',
    driverInOffice: 'Driver in office',
    opened: 'Opened',
    openingBalance: 'Opening Balance',
    retriesExhausted: 'Attempts',
    assignedToId: 'Collector',
    assignedToIds: 'Collectors',
    autoCharge: 'Auto charge',
}

// Compute today − N days as YYYY-MM-DD in local time.
function isoDaysAgo(days) {
    const d = new Date()
    d.setDate(d.getDate() - days)
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
}
function todayIso() {
    return isoDaysAgo(0)
}

function boolLabel(value) {
    if (value === true || value === 'true') return 'Yes'
    if (value === false || value === 'false') return 'No'
    return '—'
}

function formatChipValue(key, value, allValues, branches) {
    if (key === 'branchId') {
        const b = branches.find((br) => br.pk === Number(value))
        return b ? b.name : `#${value}`
    }
    if (key === 'branchIds') {
        const arr = Array.isArray(value) ? value : []
        if (arr.length === 0) return '—'
        return arr.map((branchId) => {
            const b = branches.find((br) => br.pk === Number(branchId))
            return b ? b.name : `#${branchId}`
        }).join(', ')
    }
    if (key === 'driverBillingDay') return weekdayLong(Number(value))
    if (key === 'driverStages') {
        const arr = Array.isArray(value) ? value : []
        if (arr.length === 0) return '—'
        return arr.map((v) => STAGE_LABEL_BY_VALUE[Number(v)] || `#${v}`).join(', ')
    }
    if (key === 'carStatusIn') {
        const arr = Array.isArray(value) ? value : []
        return arr.length > 0 ? arr.join(', ') : '—'
    }
    if (key === 'driverTags') {
        const arr = Array.isArray(value) ? value : []
        return arr.length > 0 ? arr.join(', ') : '—'
    }
    if (key === 'openingBalance') {
        const lo = allValues.openingBalanceGte
        const hi = allValues.openingBalanceLte
        if (lo && hi) return `$${Number(lo).toLocaleString()} – $${Number(hi).toLocaleString()}`
        if (lo) return `$${Number(lo).toLocaleString()}+`
        if (hi) return `≤ $${Number(hi).toLocaleString()}`
        return '—'
    }
    if (key === 'opened') {
        const within = allValues.openedWithinDays
        if (within != null && within !== '') {
            const n = Number(within)
            const preset = OPENED_PRESETS.find((p) => p.value === n)
            return preset ? preset.label : `Last ${n} days`
        }
        const lo = allValues.openedAtDateGte
        const hi = allValues.openedAtDateLte
        if (lo && hi) return `${lo} → ${hi}`
        if (lo) return `from ${lo}`
        if (hi) return `until ${hi}`
        return '—'
    }
    if (key === 'retriesExhausted') return value === 'true' ? 'Exhausted' : 'Open'
    if (
        key === 'hasPaymentPlan'
        || key === 'hasPaymentSchedule'
        || key === 'driverInOffice'
        || key === 'autoCharge'
    ) return boolLabel(value)
    if (key === 'assignedToId') return `User #${value}`
    if (key === 'assignedToIds') {
        const arr = Array.isArray(value) ? value : []
        if (arr.length === 0) return '—'
        return arr.length === 1 ? '1 selected' : `${arr.length} selected`
    }
    return String(value)
}

// Read filters/status/page/search from URL.
function readStateFromUrl(searchParams) {
    const filters = {}
    FILTER_KEYS.forEach((k) => {
        const v = searchParams.get(k)
        if (v == null || v === '') return
        if (
            k === 'branchId'
            || k === 'driverBillingDay'
            || k === 'assignedToId'
            || k === 'openedWithinDays'
        ) {
            filters[k] = Number(v)
        } else if (k === 'driverStages' || k === 'branchIds') {
            // CSV in URL → number array.
            const arr = v
                .split(',')
                .map((s) => Number(s.trim()))
                .filter((n) => !Number.isNaN(n))
            if (arr.length > 0) filters[k] = arr
        } else if (k === 'carStatusIn' || k === 'driverTags' || k === 'assignedToIds') {
            const arr = v
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean)
            if (arr.length > 0) filters[k] = arr
        } else if (
            k === 'hasPaymentPlan'
            || k === 'hasPaymentSchedule'
            || k === 'driverInOffice'
            || k === 'autoCharge'
        ) {
            if (v === 'true') filters[k] = true
            else if (v === 'false') filters[k] = false
        } else {
            filters[k] = v
        }
    })
    return {
        filters,
        status: searchParams.get('status') === 'CLOSED' ? 'CLOSED' : 'OPEN',
        page: Math.max(0, parseInt(searchParams.get('page') || '0', 10) || 0),
        search: searchParams.get('q') || '',
        view: searchParams.get('view') === 'admin' ? 'admin' : 'collector',
        sort: searchParams.get('sort') || '',
    }
}

function readStateFromStorage() {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY)
        if (!raw) return null
        const parsed = JSON.parse(raw)
        return {
            filters: parsed.filters || {},
            status: parsed.status === 'CLOSED' ? 'CLOSED' : 'OPEN',
            sort: typeof parsed.sort === 'string' ? parsed.sort : '',
        }
    } catch (e) {
        return null
    }
}

function urlHasState(searchParams) {
    if (!searchParams) return false
    if (searchParams.has('status') || searchParams.has('sort') || searchParams.has('q')) return true
    return FILTER_KEYS.some((k) => searchParams.has(k))
}

// First-load hierarchy: URL > localStorage > defaults.
function resolveInitialState(searchParams) {
    if (urlHasState(searchParams)) {
        return readStateFromUrl(searchParams)
    }
    const stored = readStateFromStorage()
    if (stored) {
        return {
            filters: stored.filters,
            status: stored.status,
            page: 0,
            search: '',
            view: 'collector',
            sort: stored.sort,
        }
    }
    return {
        filters: defaultFilters(),
        status: 'OPEN',
        page: 0,
        search: '',
        view: 'collector',
        sort: '',
    }
}

// Default sort field per tab when no explicit sort is set.
function defaultSort(status) {
    return status === 'OPEN' ? '-opened_at' : '-closed_at'
}

function sidebarDriverFromCase(collectionCase) {
    if (!collectionCase?.driverId) return null
    const id = toGlobalId('DriverNode', collectionCase.driverId)
    if (!id) return null
    return {
        id,
        rawId: Number(collectionCase.driverId),
    }
}

export default function CollectionsDashboard({ lockedDriverId }) {
    // When embedded under a driver page (lockedDriverId set), state stays local —
    // we don't want the dashboard's filters/sort to pollute the driver page URL.
    const embedded = lockedDriverId != null
    const [searchParams, setSearchParams] = useSearchParams()
    const initial = useMemo(
        () =>
            embedded
                ? {
                      filters: {},
                      status: 'OPEN',
                      page: 0,
                      search: '',
                      view: 'collector',
                      sort: '',
                  }
                : resolveInitialState(searchParams),
        [], // eslint-disable-line react-hooks/exhaustive-deps
    )

    const [status, setStatus] = useState(initial.status)
    const [searchInput, setSearchInput] = useState(initial.search)
    const [searchTerm, setSearchTerm] = useState(initial.search)
    const [view, setView] = useState(initial.view)
    const [page, setPage] = useState(initial.page)
    const [filters, setFilters] = useState(initial.filters)
    const [sort, setSort] = useState(initial.sort)
    const [filterOpen, setFilterOpen] = useState(false)
    const [activeSidebarDriver, setActiveSidebarDriver] = useState(null)
    const filterBtnRef = useRef(null)

    // Persist state changes back to the URL — skipped when embedded.
    useEffect(() => {
        if (embedded) return
        const next = new URLSearchParams()
        if (status !== 'OPEN') next.set('status', status)
        if (view !== 'collector') next.set('view', view)
        if (page > 0) next.set('page', String(page))
        if (searchTerm) next.set('q', searchTerm)
        if (sort) next.set('sort', sort)
        FILTER_KEYS.forEach((k) => {
            const v = filters[k]
            if (v === '' || v == null) return
            if (Array.isArray(v)) {
                if (v.length > 0) next.set(k, v.join(','))
            } else {
                next.set(k, String(v))
            }
        })
        setSearchParams(next, { replace: true })
    }, [embedded, status, view, page, searchTerm, sort, filters, setSearchParams])

    // Persist filters/status/sort to localStorage so navigating away and back
    // restores the rep's working view. Page + search stay session-only.
    useEffect(() => {
        if (embedded) return
        try {
            window.localStorage.setItem(
                STORAGE_KEY,
                JSON.stringify({ filters, status, sort }),
            )
        } catch (e) {
            /* private mode / quota — ignore */
        }
    }, [embedded, filters, status, sort])

    // Build server variables. Convert string values to the proper GQL type.
    const filterVariables = useMemo(() => {
        const v = {}
        if (filters.branchId != null) v.branchId = Number(filters.branchId)
        if (Array.isArray(filters.branchIds) && filters.branchIds.length > 0)
            v.branchIds = filters.branchIds.map((branchId) => Number(branchId))
        if (filters.driverBillingDay != null && filters.driverBillingDay !== '')
            v.driverBillingDay = Number(filters.driverBillingDay)
        if (Array.isArray(filters.driverStages) && filters.driverStages.length > 0)
            v.driverStages = filters.driverStages.map((s) => Number(s))
        if (Array.isArray(filters.carStatusIn) && filters.carStatusIn.length > 0)
            v.carStatusIn = filters.carStatusIn
        if (Array.isArray(filters.driverTags) && filters.driverTags.length > 0)
            v.driverTags = filters.driverTags
        if (filters.hasPaymentPlan === true || filters.hasPaymentPlan === false)
            v.hasPaymentPlan = filters.hasPaymentPlan
        if (filters.hasPaymentSchedule === true || filters.hasPaymentSchedule === false)
            v.hasPaymentSchedule = filters.hasPaymentSchedule
        if (filters.driverInOffice === true || filters.driverInOffice === false)
            v.driverInOffice = filters.driverInOffice
        if (filters.autoCharge === true || filters.autoCharge === false)
            v.autoCharge = filters.autoCharge
        // Relative range takes precedence over the absolute fields and is
        // expanded against today on every query, so saved views keep up with time.
        if (filters.openedWithinDays != null && filters.openedWithinDays !== '') {
            const n = Number(filters.openedWithinDays)
            if (!Number.isNaN(n) && n > 0) {
                v.openedAtDateGte = isoDaysAgo(n - 1) // include today as day 1
                v.openedAtDateLte = todayIso()
            }
        } else {
            if (filters.openedAtDateGte) v.openedAtDateGte = filters.openedAtDateGte
            if (filters.openedAtDateLte) v.openedAtDateLte = filters.openedAtDateLte
        }
        if (filters.openingBalanceGte) v.openingBalanceGte = String(filters.openingBalanceGte)
        if (filters.openingBalanceLte) v.openingBalanceLte = String(filters.openingBalanceLte)
        if (filters.retriesExhausted === 'true') v.retriesExhausted = true
        else if (filters.retriesExhausted === 'false') v.retriesExhausted = false
        if (filters.assignedToId != null)
            v.assignedToId = Number(filters.assignedToId)
        if (Array.isArray(filters.assignedToIds) && filters.assignedToIds.length > 0)
            v.assignedToIds = filters.assignedToIds
        return v
    }, [filters])

    const baseVars = useMemo(
        () => ({
            status,
            searchTerm: searchTerm || undefined,
            ...filterVariables,
            ...(lockedDriverId != null ? { driverId: Number(lockedDriverId) } : null),
        }),
        [status, searchTerm, filterVariables, lockedDriverId],
    )

    const listVariables = useMemo(
        () => ({
            ...baseVars,
            first: PAGE_SIZE,
            after: page * PAGE_SIZE || undefined,
            orderBy: [sort || defaultSort(status)],
        }),
        [baseVars, page, sort, status],
    )

    const { data, loading } = useQuery(ALL_COLLECTION_CASES, {
        variables: listVariables,
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
    })

    const { data: countData } = useQuery(COLLECTION_CASES_COUNT, {
        variables: baseVars,
        fetchPolicy: 'cache-and-network',
    })

    const { data: branchData } = useQuery(COLLECTION_BRANCHES)

    const rows = data?.allCollectionCases ?? []
    const total = countData?.collectionCasesCount ?? 0
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
    const currentPage = page + 1
    const startIndex = page * PAGE_SIZE
    const endIndex = startIndex + rows.length
    const branches = (branchData?.allBranches?.edges ?? []).map((e) => e.node)

    useEffect(() => {
        if (embedded) return
        setActiveSidebarDriver((current) => {
            if (rows.length === 0) return current ? null : current
            if (
                current
                && rows.some((row) => Number(row.driverId) === current.rawId)
            ) {
                return current
            }
            return sidebarDriverFromCase(rows[0])
        })
    }, [embedded, rows])

    const rawActiveEntries = Object.entries(filters).filter(
        ([, v]) => v !== '' && v != null && (!Array.isArray(v) || v.length > 0),
    )
    const balanceActive = BALANCE_KEYS.some((k) => filters[k] !== '' && filters[k] != null)
    const openedActive = OPENED_KEYS.some((k) => filters[k] !== '' && filters[k] != null)
    const activeFilterEntries = rawActiveEntries.filter(
        ([k]) => !BALANCE_KEYS.includes(k) && !OPENED_KEYS.includes(k),
    )
    if (balanceActive) activeFilterEntries.push(['openingBalance', null])
    if (openedActive) activeFilterEntries.push(['opened', null])
    const activeFilterCount = activeFilterEntries.length

    const onSearchChange = (e) => setSearchInput(e.target.value)
    const onSearchSubmit = (e) => {
        e.preventDefault()
        setPage(0)
        setSearchTerm(searchInput.trim())
    }

    const removeFilter = useCallback((key) => {
        setFilters((f) => {
            const next = { ...f }
            if (key === 'openingBalance') {
                BALANCE_KEYS.forEach((k) => delete next[k])
            } else if (key === 'opened') {
                OPENED_KEYS.forEach((k) => delete next[k])
            } else {
                delete next[key]
            }
            return next
        })
        setPage(0)
    }, [])

    const handleApplyFilters = useCallback((next) => {
        setFilters(next)
        setPage(0)
        setFilterOpen(false)
    }, [])

    // Three-state sort cycle per column: unsorted → desc → asc → unsorted.
    const handleSortClick = (colId) => {
        setPage(0)
        setSort((prev) => {
            if (prev === `-${colId}`) return colId
            if (prev === colId) return ''
            return `-${colId}`
        })
    }

    const sortIconFor = (colId) => {
        if (sort === `-${colId}`) return 'fa-sort-desc'
        if (sort === colId) return 'fa-sort-asc'
        return 'fa-sort'
    }

    // -------- Saved views --------

    const { data: savedViewsData } = useQuery(COLLECTIONS_SAVED_VIEWS, {
        skip: embedded,
        fetchPolicy: 'cache-and-network',
    })

    const memberId = savedViewsData?.currentUser?.member?.id
    const [savedViews, setSavedViews] = useState({})

    // Parse the JSON-encoded preference field once when it arrives.
    useEffect(() => {
        const raw = savedViewsData?.currentUser?.member?.collectionsSavedViews
        if (!raw) return
        try {
            const parsed = JSON.parse(raw)
            if (parsed && typeof parsed === 'object') {
                setSavedViews(parsed)
            }
        } catch (e) {
            /* malformed pref — start empty */
        }
    }, [savedViewsData])

    const [updateSavedViews, { loading: savingViews }] = useMutation(
        UPDATE_COLLECTIONS_SAVED_VIEWS,
    )

    // Stable signature of the current filter snapshot for active-view detection.
    const currentSignature = useMemo(
        () => JSON.stringify({ filters, status, sort }),
        [filters, status, sort],
    )

    const activeViewName = useMemo(() => {
        for (const [name, snap] of Object.entries(savedViews)) {
            try {
                if (
                    JSON.stringify({
                        filters: snap.filters || {},
                        status: snap.status || 'OPEN',
                        sort: snap.sort || '',
                    }) === currentSignature
                ) {
                    return name
                }
            } catch (e) {
                /* skip bad entry */
            }
        }
        return null
    }, [savedViews, currentSignature])

    const persistViews = useCallback(
        (next) => {
            setSavedViews(next)
            if (!memberId) return
            updateSavedViews({
                variables: { memberId, savedViews: JSON.stringify(next) },
            }).catch(() => {
                /* surfaced via toast in a follow-up; silent for now */
            })
        },
        [memberId, updateSavedViews],
    )

    const applyView = useCallback(
        (name) => {
            const snap = savedViews[name]
            if (!snap) return
            setFilters(snap.filters || {})
            setStatus(snap.status === 'CLOSED' ? 'CLOSED' : 'OPEN')
            setSort(typeof snap.sort === 'string' ? snap.sort : '')
            setPage(0)
        },
        [savedViews],
    )

    const saveCurrentAs = useCallback(
        (name) => {
            persistViews({ ...savedViews, [name]: { filters, status, sort } })
        },
        [persistViews, savedViews, filters, status, sort],
    )

    const deleteView = useCallback(
        (name) => {
            const next = { ...savedViews }
            delete next[name]
            persistViews(next)
        },
        [persistViews, savedViews],
    )

    const resetToDefaults = useCallback(() => {
        setFilters(defaultFilters())
        setStatus('OPEN')
        setSort('')
        setPage(0)
    }, [])

    const selectDriverForSidebar = useCallback((driver) => {
        if (embedded || !driver?.id) return
        setActiveSidebarDriver(driver)
    }, [embedded])

    const showOverviewToggle = !embedded
    const isAdminView = view === 'admin' && !embedded

    return (
        <div className={`cd-page${embedded ? ' embedded' : ''}`}>
            {!embedded && !isAdminView && activeSidebarDriver?.id && (
                <ExternalIntegrations driverId={activeSidebarDriver.id} />
            )}
            {showOverviewToggle && (
                <div className="cd-topbar">
                    {memberId && (
                        <SavedViewsButton
                            views={savedViews}
                            activeViewName={activeViewName}
                            onApply={applyView}
                            onSaveAs={saveCurrentAs}
                            onDelete={deleteView}
                            onReset={resetToDefaults}
                            saving={savingViews}
                        />
                    )}
                    <div className="cd-viewtoggle" role="tablist">
                        <button
                            type="button"
                            role="tab"
                            aria-selected={view === 'collector'}
                            className={view === 'collector' ? 'active' : ''}
                            onClick={() => setView('collector')}
                        >
                            <i className="fa fa-list" /> Collections
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={view === 'admin'}
                            className={view === 'admin' ? 'active' : ''}
                            onClick={() => setView('admin')}
                        >
                            <i className="fa fa-bar-chart" /> Summary
                        </button>
                    </div>
                </div>
            )}

            {isAdminView ? (
                <AdminOverview />
            ) : (
                <>
                    <div className="cd-tabsrow">
                        {TABS.map((t) => (
                            <button
                                key={t.id}
                                type="button"
                                className={`cd-tab${status === t.id ? ' active' : ''}`}
                                onClick={() => {
                                    setStatus(t.id)
                                    setPage(0)
                                }}
                            >
                                <span aria-hidden="true">{t.glyph}</span>
                                {t.label}
                                {status === t.id && total > 0 && (
                                    <span className="count">{total}</span>
                                )}
                            </button>
                        ))}
                    </div>

                    <form className="cd-searchbar" onSubmit={onSearchSubmit}>
                        <i className="fa fa-search" style={{ color: '#9ca3af' }} />
                        <input
                            type="search"
                            placeholder="Search driver or ID #"
                            value={searchInput}
                            onChange={onSearchChange}
                        />
                        <div className="cd-filterbox">
                            <button
                                ref={filterBtnRef}
                                type="button"
                                className="filters-pill"
                                onClick={() => setFilterOpen((x) => !x)}
                                aria-haspopup="dialog"
                                aria-expanded={filterOpen}
                            >
                                <i className="fa fa-sliders" />
                                Filters
                                {activeFilterCount > 0 && (
                                    <span className="count-badge">{activeFilterCount}</span>
                                )}
                            </button>
                            <FilterPopover
                                open={filterOpen}
                                anchorRef={filterBtnRef}
                                value={filters}
                                onApply={handleApplyFilters}
                                onClose={() => setFilterOpen(false)}
                                branches={branches}
                            />
                        </div>
                    </form>

                    {(searchTerm || activeFilterCount > 0) && (
                        <div className="cd-filterchips">
                            {searchTerm && (
                                <span className="cd-chip">
                                    <strong>Search:</strong> {searchTerm}
                                    <button
                                        type="button"
                                        className="chip-x"
                                        onClick={() => {
                                            setSearchInput('')
                                            setSearchTerm('')
                                            setPage(0)
                                        }}
                                        aria-label="Clear search"
                                    >
                                        ×
                                    </button>
                                </span>
                            )}
                            {activeFilterEntries.map(([key, value]) => (
                                <span key={key} className="cd-chip">
                                    <strong>{FILTER_LABELS[key] || key}:</strong>{' '}
                                    {formatChipValue(key, value, filters, branches)}
                                    <button
                                        type="button"
                                        className="chip-x"
                                        onClick={() => removeFilter(key)}
                                        aria-label={`Clear ${FILTER_LABELS[key] || key} filter`}
                                    >
                                        ×
                                    </button>
                                </span>
                            ))}
                            {activeFilterCount > 1 && (
                                <button
                                    type="button"
                                    className="cd-btn outline sm"
                                    onClick={() => {
                                        setFilters({})
                                        setPage(0)
                                    }}
                                >
                                    Clear all
                                </button>
                            )}
                        </div>
                    )}

                    <div className="cd-tablecard">
                        <div className="cd-tablescroll">
                        <table className="cd-table">
                            <thead>
                                <tr>
                                    {COLUMNS.map((col) => (
                                        <th
                                            key={col.id}
                                            className={col.sortable ? 'cd-sortable' : ''}
                                            onClick={
                                                col.sortable
                                                    ? () => handleSortClick(col.id)
                                                    : undefined
                                            }
                                            title={col.tooltip}
                                        >
                                            {col.label}
                                            {col.sortable && (
                                                <i
                                                    className={`fa ${sortIconFor(col.id)} cd-sort-icon`}
                                                />
                                            )}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {loading && rows.length === 0 && (
                                    <tr>
                                        <td colSpan={COLUMNS.length} className="cd-empty">
                                            Loading…
                                        </td>
                                    </tr>
                                )}
                                {!loading && rows.length === 0 && (
                                    <tr>
                                        <td colSpan={COLUMNS.length} className="cd-empty">
                                            No {status === 'OPEN' ? 'open' : 'closed'} cases match the current filters.
                                        </td>
                                    </tr>
                                )}
                                {rows.map((c, i) => (
                                    <CaseRow
                                        key={c.id}
                                        collectionCase={c}
                                        columnCount={COLUMNS.length}
                                        rowIndex={startIndex + i + 1}
                                        isActiveSidebarDriver={
                                            activeSidebarDriver?.rawId === Number(c.driverId)
                                        }
                                        onSelectDriverForSidebar={selectDriverForSidebar}
                                    />
                                ))}
                            </tbody>
                        </table>
                        </div>

                        {total > 0 && (
                            <div className="cd-pagination">
                                <span className="summary">
                                    {`Showing ${startIndex + 1}–${endIndex} of ${total}`}
                                </span>
                                <div className="controls">
                                    <button
                                        type="button"
                                        className="cd-btn outline sm"
                                        onClick={() => setPage((p) => Math.max(0, p - 1))}
                                        disabled={page === 0 || loading}
                                    >
                                        <i className="fa fa-chevron-left" /> Prev
                                    </button>
                                    <span className="pageinfo">
                                        Page <strong>{currentPage}</strong> of {totalPages}
                                    </span>
                                    <button
                                        type="button"
                                        className="cd-btn outline sm"
                                        onClick={() =>
                                            setPage((p) => Math.min(totalPages - 1, p + 1))
                                        }
                                        disabled={currentPage >= totalPages || loading}
                                    >
                                        Next <i className="fa fa-chevron-right" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    )
}
